// Typed client for the app's HTTP API (contract: src/shared/api.ts).

import type {
  ApiError,
  BrowserLoginStartResponse,
  CodexAuthBlob,
  CodexAuthResponse,
  CodexModelsResponse,
  DevicePollResponse,
  DeviceStartResponse,
  ErrorCode,
  LangPreference,
  MeResponse,
  OpenTab,
  SummarizeEvent,
  SummarizeRequest,
  SummaryLang,
  SummaryRecord,
  TabsResponse,
  VideoTranscript,
} from '../../shared/api';
import { langPreferenceParams } from '../../shared/lang';
import { readServerSentEvents, type ServerSentEvent } from '../../shared/sse';

/** Error codes of the API plus client-side failures. */
export type ClientErrorCode = ErrorCode | 'network' | 'aborted' | 'stream_interrupted';

export class ApiRequestError extends Error {
  readonly code: ClientErrorCode;
  readonly status: number | undefined;
  /** Seconds from a Retry-After header (HTTP 429/503). */
  readonly retryAfter: number | undefined;

  constructor(code: ClientErrorCode, message: string, status?: number, retryAfter?: number) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

export function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof ApiRequestError && error.code === 'aborted') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

const ERROR_CODES: ReadonlySet<string> = new Set<ErrorCode>([
  'bad_request',
  'login_required',
  'codex_auth_required',
  'codex_auth_invalid',
  'codex_quota',
  'video_not_found',
  'transcript_unavailable',
  'youtube_blocked',
  'rate_limited',
  'job_not_found',
  'internal',
]);

function toErrorCode(code: unknown, status: number): ErrorCode {
  if (typeof code === 'string' && ERROR_CODES.has(code)) return code as ErrorCode;
  if (status === 401) return 'login_required';
  if (status === 404) return 'video_not_found';
  if (status === 429) return 'rate_limited';
  if (status >= 400 && status < 500) return 'bad_request';
  return 'internal';
}

/** Retry-After in seconds (delta-seconds or HTTP date). */
export function parseRetryAfter(value: string | null, now = Date.now()): number | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const date = Date.parse(trimmed);
  return Number.isNaN(date) ? undefined : Math.max(0, Math.ceil((date - now) / 1000));
}

async function errorFromResponse(res: Response): Promise<ApiRequestError> {
  let code: unknown;
  let message = `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as Partial<ApiError>;
    code = body.error?.code;
    if (body.error?.message) message = body.error.message;
  } catch {
    // Not JSON (e.g. a proxy error page).
  }
  return new ApiRequestError(toErrorCode(code, res.status), message, res.status, parseRetryAfter(res.headers.get('retry-after')));
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal | undefined;
  headers?: Record<string, string>;
}

async function request(path: string, options: RequestOptions = {}): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
    credentials: 'same-origin',
    signal: options.signal ?? null,
  };
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(options.body);
  }
  try {
    return await fetch(path, init);
  } catch (error) {
    if (isAbortError(error)) throw new ApiRequestError('aborted', 'Abgebrochen');
    throw new ApiRequestError('network', error instanceof Error ? error.message : 'Network error');
  }
}

async function requestJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const res = await request(path, options);
  if (!res.ok) throw await errorFromResponse(res);
  try {
    return (await res.json()) as T;
  } catch {
    throw new ApiRequestError('internal', 'Ungültige Antwort vom Server', res.status);
  }
}

// ---------------------------------------------------------------------------
// Endpoints

/** GET /api/me */
export function getMe(signal?: AbortSignal): Promise<MeResponse> {
  return requestJson<MeResponse>('/api/me', { signal });
}

/**
 * GET /api/summary – cached summary or null (404). `lang`: an explicit language, or the
 * preference (the server picks the summary matching the video's spoken language).
 */
export async function getCachedSummary(
  videoId: string,
  lang: SummaryLang | LangPreference,
  signal?: AbortSignal,
): Promise<SummaryRecord | null> {
  const params = new URLSearchParams({ videoId, ...(typeof lang === 'string' ? { lang } : langPreferenceParams(lang)) });
  const res = await request(`/api/summary?${params}`, { signal });
  if (res.status === 404) return null;
  if (!res.ok) throw await errorFromResponse(res);
  return (await res.json()) as SummaryRecord;
}

/** GET /api/transcript – the transcript stored with the video's summaries, or null (404). */
export async function getStoredTranscript(videoId: string, signal?: AbortSignal): Promise<VideoTranscript | null> {
  const res = await request(`/api/transcript?${new URLSearchParams({ videoId })}`, { signal });
  if (res.status === 404) return null;
  if (!res.ok) throw await errorFromResponse(res);
  return (await res.json()) as VideoTranscript;
}

/** POST /api/transcript – the video's transcript, fetched by the server (signed-in users only). */
export function getServerTranscript(videoId: string, lang: SummaryLang | undefined, signal?: AbortSignal): Promise<VideoTranscript> {
  return requestJson<VideoTranscript>('/api/transcript', { method: 'POST', body: { videoId, ...(lang ? { lang } : {}) }, signal });
}

/** GET /api/tabs – open tabs of the signed-in user. */
export async function getTabs(signal?: AbortSignal): Promise<OpenTab[]> {
  return (await requestJson<TabsResponse>('/api/tabs', { signal })).tabs;
}

/** PUT /api/tabs/:videoId – insert or update a tab; answers with the full list. */
export async function putTab(tab: Pick<OpenTab, 'videoId' | 'lang' | 'title' | 'channel'>, signal?: AbortSignal): Promise<OpenTab[]> {
  const body = { ...(tab.lang ? { lang: tab.lang } : {}), title: tab.title, channel: tab.channel };
  return (await requestJson<TabsResponse>(`/api/tabs/${encodeURIComponent(tab.videoId)}`, { method: 'PUT', body, signal })).tabs;
}

/** DELETE /api/tabs/:videoId – idempotent; answers with the full list. */
export async function deleteTab(videoId: string, signal?: AbortSignal): Promise<OpenTab[]> {
  return (await requestJson<TabsResponse>(`/api/tabs/${encodeURIComponent(videoId)}`, { method: 'DELETE', signal })).tabs;
}

/** POST /api/tabs/import – merges tabs (existing server tabs win); answers with the full list. */
export async function importTabs(tabs: OpenTab[], signal?: AbortSignal): Promise<OpenTab[]> {
  return (await requestJson<TabsResponse>('/api/tabs/import', { method: 'POST', body: { tabs }, signal })).tabs;
}

/** POST /api/codex/device/start */
export function startCodexDeviceLogin(signal?: AbortSignal): Promise<DeviceStartResponse> {
  return requestJson<DeviceStartResponse>('/api/codex/device/start', { method: 'POST', signal });
}

/** POST /api/codex/device/poll */
export function pollCodexDeviceLogin(loginId: string, signal?: AbortSignal): Promise<DevicePollResponse> {
  return requestJson<DevicePollResponse>('/api/codex/device/poll', {
    method: 'POST',
    body: { loginId },
    signal,
  });
}

/** POST /api/codex/browser/start – sign-in via redirect URL (no device-code setting needed). */
export function startCodexBrowserLogin(signal?: AbortSignal): Promise<BrowserLoginStartResponse> {
  return requestJson<BrowserLoginStartResponse>('/api/codex/browser/start', { method: 'POST', signal });
}

/** POST /api/codex/browser/complete – the address of the (unreachable) callback page the user landed on. */
export function completeCodexBrowserLogin(
  loginId: string,
  redirectUrl: string,
  signal?: AbortSignal,
): Promise<DevicePollResponse> {
  return requestJson<DevicePollResponse>('/api/codex/browser/complete', {
    method: 'POST',
    body: { loginId, redirectUrl },
    signal,
  });
}

/** POST /api/codex/auth – validate/store a pasted or browser-stored auth.json. */
export function saveCodexAuth(auth: CodexAuthBlob, signal?: AbortSignal): Promise<CodexAuthResponse> {
  return requestJson<CodexAuthResponse>('/api/codex/auth', { method: 'POST', body: { auth }, signal });
}

/** POST /api/codex/models – the models of the stored login, else of the browser-stored `codexAuth`. */
export function getCodexModels(codexAuth: CodexAuthBlob | undefined, signal?: AbortSignal): Promise<CodexModelsResponse> {
  return requestJson<CodexModelsResponse>('/api/codex/models', {
    method: 'POST',
    body: codexAuth ? { codexAuth } : {},
    signal,
  });
}

/** DELETE /api/codex/auth – removes the server-stored login. */
export async function deleteCodexAuth(signal?: AbortSignal): Promise<void> {
  const res = await request('/api/codex/auth', { method: 'DELETE', signal });
  if (!res.ok && res.status !== 404) throw await errorFromResponse(res);
}

/** A SummarizeEvent with its position in the job (SSE `id:`), if the server sent one. */
export type IndexedSummarizeEvent = SummarizeEvent & { index?: number };

export interface StreamOptions {
  /** Called once the server accepted the request and the event stream starts. */
  onOpen?: () => void;
  /**
   * Called for every received chunk, keep-alive comments included (liveness of the
   * connection; see the jobManager's reconnect logic).
   */
  onActivity?: () => void;
}

/**
 * POST /api/summarize – yields the server-sent events. Pre-stream failures
 * (HTTP 4xx/5xx with an ApiError body) are thrown as ApiRequestError;
 * failures during the stream arrive as `{ type: 'error' }` events.
 * Throws `stream_interrupted` if the stream ends without `done`/`error`.
 */
export async function* summarize(
  body: SummarizeRequest,
  signal?: AbortSignal,
  options: StreamOptions = {},
): AsyncGenerator<IndexedSummarizeEvent, void, void> {
  const res = await request('/api/summarize', {
    method: 'POST',
    body,
    signal,
    headers: { Accept: 'text/event-stream' },
  });
  if (!res.ok) throw await errorFromResponse(res);
  yield* summaryEventStream(res, signal, options);
}

/**
 * GET /api/summarize/jobs/:jobId?after=<index> – re-attaches to a running (or recently
 * finished) summary job: replays the events after `after`, then follows it live.
 * Unknown or expired job: throws `job_not_found`.
 */
export async function* attachSummaryJob(
  jobId: string,
  after: number | undefined,
  signal?: AbortSignal,
  options: StreamOptions = {},
): AsyncGenerator<IndexedSummarizeEvent, void, void> {
  const query = after !== undefined ? `?after=${after}` : '';
  const res = await request(`/api/summarize/jobs/${encodeURIComponent(jobId)}${query}`, {
    signal,
    headers: { Accept: 'text/event-stream' },
  });
  if (!res.ok) {
    const error = await errorFromResponse(res);
    if (res.status === 404) throw new ApiRequestError('job_not_found', error.message, 404);
    throw error;
  }
  yield* summaryEventStream(res, signal, options);
}

/**
 * DELETE /api/summarize/jobs/:jobId – cancels a job (fire-and-forget; `keepalive` lets it
 * survive the page going away right after, e.g. closing the last tab).
 */
export function cancelSummaryJob(jobId: string): void {
  try {
    fetch(`/api/summarize/jobs/${encodeURIComponent(jobId)}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      keepalive: true,
    }).catch(() => {});
  } catch {
    // ignore
  }
}

async function* summaryEventStream(
  res: Response,
  signal: AbortSignal | undefined,
  options: StreamOptions,
): AsyncGenerator<IndexedSummarizeEvent, void, void> {
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    // Tolerate a server answering with a plain JSON summary or error.
    const json = (await res.json()) as Partial<ApiError> & Partial<SummaryRecord>;
    if (json.error) throw new ApiRequestError(toErrorCode(json.error.code, res.status), json.error.message);
    yield { type: 'done', summary: json as SummaryRecord };
    return;
  }
  if (!res.body) throw new ApiRequestError('stream_interrupted', 'Leere Antwort vom Server');
  options.onOpen?.();

  let finished = false;
  try {
    for await (const message of readServerSentEvents(res.body, signal, options.onActivity)) {
      const event = toSummarizeEvent(message);
      if (!event) continue;
      if (event.type === 'done' || event.type === 'error') finished = true;
      yield event;
      if (finished) return;
    }
  } catch (error) {
    if (isAbortError(error) || signal?.aborted) throw new ApiRequestError('aborted', 'Abgebrochen');
    throw new ApiRequestError('stream_interrupted', error instanceof Error ? error.message : 'Stream error');
  }
  if (signal?.aborted) throw new ApiRequestError('aborted', 'Abgebrochen');
  if (!finished) throw new ApiRequestError('stream_interrupted', 'Die Verbindung wurde unterbrochen');
}

const EVENT_TYPES = new Set(['job', 'status', 'lang', 'meta', 'delta', 'progress', 'auth', 'done', 'error']);

/**
 * Maps one SSE message (`event: <type>`, `data: <JSON of the rest>`, `id: <index>`) to a
 * SummarizeEvent; a numeric `id` becomes `index`.
 */
export function toSummarizeEvent(message: ServerSentEvent): IndexedSummarizeEvent | null {
  let payload: Record<string, unknown>;
  try {
    const parsed: unknown = JSON.parse(message.data);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    payload = parsed as Record<string, unknown>;
  } catch {
    return null;
  }
  const type = message.event !== 'message' ? message.event : payload.type;
  if (typeof type !== 'string' || !EVENT_TYPES.has(type)) return null;
  const event = { ...payload, type } as IndexedSummarizeEvent;
  delete event.index;
  const index = message.id !== undefined && /^\d+$/.test(message.id.trim()) ? Number(message.id.trim()) : undefined;
  if (index !== undefined) event.index = index;
  return event;
}
